import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { normalizePersonName, type TimeEntryDocType } from '@ast-data/shared';
import { DataGridPro, useGridApiRef } from '../../lib/dataGrid';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import ConfirmDialog from '../../components/ConfirmDialog';
import GridCrudToolbar from '../../components/GridCrudToolbar';
import { useInlineRowEditing } from '../../components/useInlineRowEditing';
import { removeById } from '../../db/removeById';
import { useValidationMessage } from '../../i18n/validationMessages';
import { isoWeekYear } from '../timeTracking/timeMath';
import { loadDrafts, saveDrafts } from '../timeTracking/timeEntryDraftStore';
import { queryTimeEntryOverlaps } from '../timeTracking/overlap';
import TimeEntryOverlapNotice from '../timeTracking/TimeEntryOverlapNotice';
import { useTimeEntryOverlaps } from '../timeTracking/useTimeEntryOverlaps';
import {
    useTimeEntryWarnings,
    timeEntryRowSx
} from '../timeTracking/timeEntryFlags';
import { useActiveUsers } from '../users/useActiveUsers';
import { useUserLabel } from '../users/useUserLabel';
import { useAccountResolver } from '../users/personUserId';
import { useTimeEntryNameOptions } from '../timeTracking/useTimeEntryNameOptions';
import { type TimeEntryVariant } from '../timeTracking/timeEntryVariant';
import TimeEntryFormDialog from '../timeTracking/TimeEntryFormDialog';
import TimeEntryTotalsFooter from '../timeTracking/TimeEntryTotalsFooter';
import { useFieldTimeEntryColumns } from './useFieldTimeEntryColumns';
import { isRowComplete, fieldRowSaveError } from './fieldTimeEntryRow';
import { fieldFullBleedSx, fieldFullBleedGridSx } from './fieldPageMetrics';
import { compareNatural } from '../../utils/compare';
import { useShownJson } from '../users/personLabels';

/**
 * Move focus to the next editable input within the same grid row (used to make
 * Enter behave like Tab — the soft keyboard's "next" on mobile has no Tab key).
 * Returns false when already on the last input, or when `current` is not one of
 * the row's inputs at all.
 */
function focusNextInputInRow(current: HTMLElement | null): boolean {
    const rowEl = current?.closest('[role="row"]');
    if (!rowEl || !current) {
        return false;
    }
    const inputs = Array.from(
        rowEl.querySelectorAll<HTMLInputElement>('input')
    ).filter((el) => el.type !== 'hidden' && !el.readOnly && !el.disabled);
    const index = inputs.indexOf(current as HTMLInputElement);
    // Not an input of this row (a row action button, say). Without this guard
    // indexOf's -1 would make the "next" input the FIRST one, jumping focus back
    // to the start of the row.
    if (index === -1) {
        return false;
    }
    const next = inputs[index + 1];
    if (next) {
        next.focus();
        return true;
    }
    return false;
}

/**
 * This grid's new rows are real draft docs in `gridRows` (mirrored to
 * localStorage), not the single sentinel draft useInlineRowEditing can hold — so
 * only its row-mode half is used, exactly as the office grid does.
 */
const NO_DRAFT = '';

interface FieldTimeEntryGridProps {
    /** Which Zeiterfassung this grid records — Mitarbeiter or Geräte. */
    variant: TimeEntryVariant;
    projectId: string;
    /** The selected workday ('YYYY-MM-DD') — rows are scoped to it. */
    day: string;
}

/**
 * The field day view's editable grid, for either Zeiterfassung.
 *
 * ADDING opens the fullscreen editor (`TimeEntryFormDialog`), the same one the
 * office uses: a new entry has seven fields, and typing them across a grid row
 * on a tablet was the worst place in the app to do it.
 *
 * CORRECTING depends on the screen. A tablet edits the row INLINE — Tab/Enter
 * movement, derived net + phase, and a commit that would leave the row
 * incomplete is rejected. A phone opens the fullscreen editor instead: its row
 * inputs are narrower than a fingertip and the grid scrolls sideways while they
 * are open.
 *
 * The LOCAL DRAFT machinery below is what adding used to do. It stays only for
 * as long as drafts can still be sitting on somebody's tablet — they render and
 * can be saved, but no new ones are made.
 */
export default function FieldTimeEntryGrid({
    variant,
    projectId,
    day
}: FieldTimeEntryGridProps) {
    const intl = useIntl();
    const validationMessage = useValidationMessage();
    const apiRef = useGridApiRef();
    const collection = useRxCollection<TimeEntryDocType>('timeEntries');
    const theme = useTheme();
    // Phones correct a row in the dialog instead of inline (see the note above).
    const editInDialog = useMediaQuery(theme.breakpoints.down('sm'));

    const savedRowsQuery = useStableQuery<TimeEntryDocType>({
        selector: {
            projectId,
            date: day,
            entryType: variant.type
        }
    });
    const { results: savedRowsDocs, loading: loading } =
        useLiveRxQuery<TimeEntryDocType>({
            collection: 'timeEntries',
            query: savedRowsQuery
        });
    const savedRows = useShownJson<TimeEntryDocType>(
        'timeEntry',
        savedRowsDocs
    );
    const users = useActiveUsers();
    // Resolve accounts to their display name (custom name → username) for both the
    // suggestions and the save-time name→account link so they agree.
    const nameOf = useUserLabel();

    const ability = useAbility();
    const subject = scopedSubject('TimeEntry', { projectId });
    const canCreate = ability.can('create', subject);
    const canEdit = ability.can('update', subject);

    // Ids of entries double-booked with another (post-sync flag) — tinted red.
    const conflicts = useTimeEntryOverlaps();
    // ArbZG break/rest warnings per entry (spans all projects + days) — amber.
    const warnings = useTimeEntryWarnings();

    // Unsynced draft rows for this project + day, mirrored to localStorage. A ref
    // shadows the state so async handlers (Save/discard, fired from a tap that may
    // first blur-commit an in-progress edit) always read the freshest drafts.
    const [drafts, setDrafts] = useState<TimeEntryDocType[]>(() =>
        loadDrafts(projectId, day, variant.type)
    );
    const draftsRef = useRef<TimeEntryDocType[]>(drafts);
    useEffect(() => {
        // Switching project or day keeps this component mounted, so the drafts
        // are re-read here. The only OTHER writer is commitDrafts, which keeps
        // the ref in step itself — nothing sets the state behind the ref's back.
        const reloaded = loadDrafts(projectId, day, variant.type);
        draftsRef.current = reloaded;
        // State and ref have to move together, which a render-phase seed cannot
        // do without writing the ref during render (react-hooks/refs).
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDrafts(reloaded);
    }, [projectId, day, variant.type]);

    // Every draft mutation goes through here so state + ref + localStorage stay in
    // sync (persist with the CURRENT project/day, never a stale one).
    function commitDrafts(next: TimeEntryDocType[]) {
        draftsRef.current = next;
        setDrafts(next);
        saveDrafts(projectId, day, variant.type, next);
    }

    const draftIds = useMemo(
        () => new Set(drafts.map((draft) => draft.id)),
        [drafts]
    );

    const gridRows = useMemo(
        () => [
            ...[...savedRows].sort(
                (firstRow, secondRow) =>
                    compareNatural(firstRow.workerName, secondRow.workerName) ||
                    firstRow.startTime.localeCompare(secondRow.startTime)
            ),
            ...drafts
        ],
        [savedRows, drafts]
    );

    const nameOptions = useTimeEntryNameOptions(variant, users, nameOf);
    // The one name -> account rule, shared with the office save path.
    const resolveAccount = useAccountResolver();

    // Row modes are controlled so the actions column can tell an open row from a
    // closed one. The apiRef calls below (startRowEditMode etc.) keep this model
    // in sync — the grid publishes every mode change back through it.
    const { rowModesModel, setRowModesModel, isEditing, requestSave, cancel } =
        useInlineRowEditing<TimeEntryDocType>(NO_DRAFT);

    const [deleteTarget, setDeleteTarget] = useState<TimeEntryDocType | null>(
        null
    );
    // A node, not a string: a refused double-booking carries a button that opens
    // the blocking entry, and such a message must STAY put — a notice that fades
    // after four seconds cannot hold something to click.
    const [notice, setNotice] = useState<{
        message: ReactNode;
        sticky?: boolean;
    } | null>(null);
    // A just-added draft whose Name cell should open for editing once it renders.
    const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    // The saved row a phone is correcting in the fullscreen editor (null = none).
    const [editEntry, setEditEntry] = useState<TimeEntryDocType | null>(null);

    /**
     * Open a row for correction — in the dialog on a phone, inline on a tablet.
     *
     * A legacy local draft always stays inline: it does not exist in RxDB yet, so
     * the editor (which writes a record) has nothing to edit. Its ✓ is what
     * promotes it, exactly as before.
     */
    function editRow(row: TimeEntryDocType) {
        if (editInDialog && !draftIds.has(row.id)) {
            setEditEntry(row);
            return;
        }
        // startRowEditMode throws ("not in view mode") on a row already open —
        // the pencil stays visible while the row edits, and a cell tap can land
        // on an open row too.
        if (!apiRef.current || apiRef.current.getRowMode(row.id) === 'edit') {
            return;
        }
        apiRef.current.startRowEditMode({
            id: row.id,
            fieldToFocus: 'workerName'
        });
    }

    useEffect(() => {
        if (!pendingFocusId) {
            return;
        }
        if (!apiRef.current?.getRow?.(pendingFocusId)) {
            return;
        }
        apiRef.current.startRowEditMode({
            id: pendingFocusId,
            fieldToFocus: 'workerName'
        });
        setPendingFocusId(null);
    }, [gridRows, pendingFocusId, apiRef]);

    // The blocking notice for a locally-known time overlap (same worker, day) —
    // the same component the office grid and the form dialog show, so all three
    // name the conflicting project and open the same read-only dialog on it.
    function overlapNotice(conflict: TimeEntryDocType) {
        return {
            sticky: true,
            message: (
                <TimeEntryOverlapNotice overlap={conflict} variant={variant} />
            )
        };
    }

    // A field save-guard code (see fieldRowSaveError) as a user-facing message:
    // rule codes localize via the shared validationMessage (so they read exactly
    // like the office grid), the `incomplete` sentinel uses the row-level string.
    function saveGuardMessage(code: string): string {
        return code === 'incomplete'
            ? intl.formatMessage({ id: 'timeEntry.incompleteRow' })
            : validationMessage(code);
    }

    /**
     * The row as it will be stored: trimmed name plus the account it links to
     * (worker entries only — a Gerät is not a person and never links).
     *
     * MUST be applied BEFORE the overlap check. Overlap identity prefers the
     * account link (sameTimeEntryPerson), so a row whose userId still points at
     * the PREVIOUS worker would compare the new name against the old person's
     * entries. The office save path resolves in this order too.
     *
     * On an edit with an unchanged name the stored link is KEPT rather than
     * worked out again: it may never have come from the name (the Stundenzettel
     * import, the Zeitabgleich) and the account may have been renamed since —
     * editing an end time is no reason to lose whose day it is.
     */
    function withResolvedWorker(
        row: TimeEntryDocType,
        stored?: TimeEntryDocType
    ): TimeEntryDocType {
        const workerName = `${row.workerName ?? ''}`.trim();
        const unchanged =
            stored &&
            normalizePersonName(stored.workerName) ===
                normalizePersonName(workerName);
        const kept = unchanged ? stored.userId : '';
        const matched = variant.linkUsers
            ? kept || (resolveAccount(workerName)?.id ?? '')
            : '';
        return { ...row, workerName, userId: matched };
    }

    // Promote a completed draft into RxDB (where it syncs) and drop it locally —
    // unless it overlaps another entry for this worker that day (any project).
    async function saveDraft(draftId: string) {
        const draft = draftsRef.current.find(
            (candidate) => candidate.id === draftId
        );
        if (!collection || !draft) {
            return;
        }
        // Same guard as the office save path: content validity (end-after-start,
        // break-within-work), completeness, and not-in-the-future — with the same
        // messages. Overlap is checked next (it needs the collection + drafts).
        const candidate = withResolvedWorker(draft);
        const saveError = fieldRowSaveError(candidate);
        if (saveError) {
            setNotice({ message: saveGuardMessage(saveError) });
            return;
        }
        const overlaps = await queryTimeEntryOverlaps(
            collection,
            candidate,
            // The other drafts get the same treatment, so a draft-vs-draft clash
            // is matched on the same identity as a draft-vs-saved one.
            draftsRef.current
                .filter((candidate) => candidate.id !== draft.id)
                .map((other) => withResolvedWorker(other))
        );
        if (overlaps.length > 0) {
            setNotice(overlapNotice(overlaps[0]));
            return;
        }
        const { year, week } = isoWeekYear(candidate.date);
        const now = new Date().toISOString();
        await collection.insert({
            ...candidate,
            year,
            week,
            // A draft written before the Anzahl existed carries none, and the
            // schema requires one the moment the row is promoted into RxDB.
            quantity: Number(candidate.quantity) || 1,
            note: candidate.note.trim(),
            createdBy: '',
            updatedBy: '',
            updatedAt: now,
            createdAt: now
        });
        commitDrafts(
            draftsRef.current.filter((candidate) => candidate.id !== draft.id)
        );
    }

    function discardDraft(draftId: string) {
        commitDrafts(
            draftsRef.current.filter((candidate) => candidate.id !== draftId)
        );
    }

    async function confirmDelete() {
        if (deleteTarget) {
            await removeById(collection, deleteTarget.id);
        }
        setDeleteTarget(null);
    }

    // A draft edit stays local; a saved-row edit is written to RxDB only if it
    // remains complete (so a saved row can never be edited into an invalid,
    // still-syncing state).
    async function processRowUpdate(
        newRow: TimeEntryDocType
    ): Promise<TimeEntryDocType> {
        if (draftIds.has(newRow.id)) {
            commitDrafts(
                draftsRef.current.map((candidate) =>
                    candidate.id === newRow.id
                        ? { ...candidate, ...newRow }
                        : candidate
                )
            );
            return newRow;
        }
        if (!collection) {
            return newRow;
        }
        // Read the entry first: an unchanged name keeps the account link it
        // already carries (see withResolvedWorker).
        const doc = await collection.findOne(newRow.id).exec();
        // Same guard + messages as saveDraft and the office grid. The message is
        // set here (not in onProcessRowUpdateError) so an overlap can show the
        // detailed conflicting-row wording; throwing keeps the row in edit mode.
        const candidate = withResolvedWorker(
            newRow,
            doc?.toJSON() as TimeEntryDocType | undefined
        );
        const saveError = fieldRowSaveError(candidate);
        if (saveError) {
            setNotice({ message: saveGuardMessage(saveError) });
            throw new Error(saveError);
        }
        const overlaps = await queryTimeEntryOverlaps(collection, candidate);
        if (overlaps.length > 0) {
            setNotice(overlapNotice(overlaps[0]));
            throw new Error('overlap');
        }
        if (doc) {
            await doc.incrementalPatch({
                workerName: candidate.workerName,
                quantity: Number(candidate.quantity) || 1,
                startTime: `${candidate.startTime ?? ''}`,
                endTime: `${candidate.endTime ?? ''}`,
                pauseMinutes: Number(candidate.pauseMinutes) || 0,
                // Trimmed like the office save path (see saveTimeEntry).
                note: `${candidate.note ?? ''}`.trim(),
                userId: candidate.userId,
                updatedAt: new Date().toISOString()
            });
        }
        return candidate;
    }

    const columns = useFieldTimeEntryColumns({
        variant,
        canEdit,
        conflicts,
        warnings,
        nameOptions,
        draftIds,
        onSaveDraft: saveDraft,
        onDiscardDraft: discardDraft,
        onEditRow: editRow,
        isEditing,
        onSaveRow: requestSave,
        onCancelRow: cancel,
        onDelete: setDeleteTarget
    });

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box
                sx={(theme) => ({
                    /**
                     * Fill whatever the page leaves under the day stepper — the
                     * chain of `flex: 1` starts at the field layout's content
                     * area. `minHeight: 0` lets it shrink below its rows so the
                     * grid scrolls internally instead of pushing the page.
                     */
                    flex: 1,
                    minHeight: 0,
                    /**
                     * A flex column, so the grid grows by flex-grow rather than
                     * by `height: 100%`. That percentage would collapse to `auto`:
                     * the layout root sizes itself with `min-height: 100dvh`,
                     * which never makes a height *definite*, so no descendant
                     * percentage height can resolve.
                     */
                    display: 'flex',
                    flexDirection: 'column',
                    ...fieldFullBleedSx(theme)
                })}
            >
                <DataGridPro
                    apiRef={apiRef}
                    rows={gridRows}
                    columns={columns}
                    loading={loading}
                    // Row edit mode: the whole row's inputs render at once, so Tab
                    // (and Enter, below) moves focus input→input across the row.
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={setRowModesModel}
                    /**
                     * NB: the office grid also passes the hook's
                     * `handleRowEditStop` to stop a tap outside the row from
                     * committing. Not here — tap-away IS how the field commits.
                     * Save/Cancel still work: MUI raises rowFocusOut only when
                     * focus lands on ANOTHER row, and they sit in this one.
                     */
                    processRowUpdate={processRowUpdate}
                    // The message is already set inside processRowUpdate (so an
                    // overlap keeps its detailed wording); the throw only needs to
                    // keep the row in edit mode.
                    onProcessRowUpdateError={() => {}}
                    // A cell tap can never start an editor a phone is not meant
                    // to use — not by double-tap, and not from a stray
                    // startRowEditMode either. The legacy drafts are the one
                    // exception (see editRow).
                    isCellEditable={(params) =>
                        !editInDialog || draftIds.has(params.row.id)
                    }
                    // Single tap opens the whole row for editing, focusing the
                    // tapped field (no double-tap) — or, on a phone, opens the
                    // row in the fullscreen editor.
                    onCellClick={(params) => {
                        if (
                            !canEdit ||
                            !params.colDef.editable ||
                            apiRef.current?.getRowMode(params.id) === 'edit'
                        ) {
                            return;
                        }
                        if (editInDialog && !draftIds.has(params.row.id)) {
                            setEditEntry(params.row);
                            return;
                        }
                        apiRef.current?.startRowEditMode({
                            id: params.id,
                            fieldToFocus: params.field
                        });
                    }}
                    // Enter advances to the next field instead of committing —
                    // matches Tab, and lets mobile soft keyboards (no Tab) move on.
                    onCellKeyDown={(params, event) => {
                        if (
                            event.key !== 'Enter' ||
                            !canEdit ||
                            apiRef.current?.getRowMode(params.id) !== 'edit'
                        ) {
                            return;
                        }
                        // Only while typing in a field. On a row action (Save /
                        // Cancel) Enter must activate the button, so leave it to
                        // the button rather than pulling focus back into the row.
                        const active = document.activeElement;
                        if (!(active instanceof HTMLInputElement)) {
                            return;
                        }
                        event.defaultMuiPrevented = true;
                        const moved = focusNextInputInRow(active);
                        if (!moved) {
                            apiRef.current?.stopRowEditMode({ id: params.id });
                        }
                    }}
                    getRowClassName={(params) => {
                        if (conflicts.has(params.row.id)) {
                            return 'row-conflict';
                        }
                        if ((warnings.get(params.row.id) ?? []).length > 0) {
                            return 'row-warning';
                        }
                        return isRowComplete(params.row)
                            ? ''
                            : 'row-incomplete';
                    }}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    showToolbar
                    slots={{
                        toolbar: GridCrudToolbar,
                        footer: TimeEntryTotalsFooter
                    }}
                    slotProps={{
                        toolbar: {
                            onAdd: () => setAddOpen(true),
                            canAdd: canCreate,
                            addMessageId: variant.addId
                        }
                    }}
                    sx={(theme) => ({
                        // Seamless on phones: no outline or rounded corners, so
                        // the grid reads as the page (see fieldFullBleedSx).
                        ...fieldFullBleedGridSx(theme),
                        // Tint rows that still need a field (see isRowComplete) —
                        // i.e. every unsaved draft until it's ready to save.
                        '& .row-incomplete': {
                            backgroundColor: 'rgba(237, 108, 2, 0.08)'
                        },
                        // A cell with an invalid value (end ≤ start, future, break ≥
                        // work) — the non-blocking equivalent of the office grid's
                        // cell error tint (native cells don't set `Mui-error`).
                        '& .cell-invalid': {
                            backgroundColor: alpha(
                                theme.palette.error.main,
                                0.12
                            ),
                            color: theme.palette.error.main
                        },
                        // Red = double-booking conflict; amber = ArbZG break/rest
                        // warning (both explained by the leading flag icon).
                        ...timeEntryRowSx
                    })}
                />
            </Box>

            {/* Adding goes through the same fullscreen editor as everywhere
                else — it picks the day's project and date itself, so the field
                only has to say which day it is looking at. On a phone this is
                also where a saved row is corrected, so the one dialog serves
                both: an `entry` makes it the editor, none makes it the adder. */}
            <TimeEntryFormDialog
                variant={variant}
                open={addOpen || !!editEntry}
                onClose={() => {
                    setAddOpen(false);
                    setEditEntry(null);
                }}
                entry={editEntry}
                projectId={projectId}
                defaultDate={day}
            />

            <ConfirmDialog
                open={!!deleteTarget}
                title={<FormattedMessage id={variant.deleteTitleId} />}
                message={
                    <FormattedMessage
                        id={variant.deleteConfirmId}
                        values={{ worker: deleteTarget?.workerName ?? '' }}
                    />
                }
                confirmColor="error"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
            <Snackbar
                open={!!notice}
                autoHideDuration={notice?.sticky ? null : 4000}
                onClose={() => setNotice(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="warning"
                    variant="filled"
                    onClose={() => setNotice(null)}
                >
                    {notice?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
